/**
 * 🔧 Web Worker 后台定时器
 *
 * 为什么需要这个？
 * - 主线程的 setInterval/setTimeout 在后台标签页会被浏览器严重节流：
 *   - Chrome: 后台标签页 setInterval 最小间隔 1 分钟
 *   - iOS Safari: 后台 30 秒后可能完全冻结 JS 执行
 *   - Android Chrome: 后台 5 分钟后开始节流
 * - Web Worker 的定时器不受主线程节流策略影响
 *   - Worker 线程独立运行，浏览器不会对其应用相同的节流规则
 *   - 即使主线程被冻结，Worker 仍可能继续执行（取决于 OS 级别的限制）
 *
 * 使用方式：
 * ```ts
 * const timer = createWorkerTimer()
 * const id = timer.setInterval(() => console.log('tick'), 1000)
 * timer.setTimeout(() => console.log('once'), 5000)
 * timer.clearInterval(id)
 * timer.terminate() // 销毁 Worker
 * ```
 */

// Worker 内部代码（作为字符串，用于创建 Blob Worker）
const WORKER_CODE = `
// 存储所有活跃的定时器
const timers = new Map();

self.onmessage = function(e) {
  const { type, id, delay } = e.data;

  switch (type) {
    case 'setInterval': {
      const timerId = setInterval(() => {
        self.postMessage({ type: 'tick', id });
      }, delay);
      timers.set(id, { type: 'interval', timerId });
      break;
    }
    case 'setTimeout': {
      const timerId = setTimeout(() => {
        self.postMessage({ type: 'tick', id });
        timers.delete(id);
      }, delay);
      timers.set(id, { type: 'timeout', timerId });
      break;
    }
    case 'clearInterval':
    case 'clearTimeout': {
      const timer = timers.get(id);
      if (timer) {
        if (timer.type === 'interval') {
          clearInterval(timer.timerId);
        } else {
          clearTimeout(timer.timerId);
        }
        timers.delete(id);
      }
      break;
    }
    case 'clearAll': {
      for (const [, timer] of timers) {
        if (timer.type === 'interval') {
          clearInterval(timer.timerId);
        } else {
          clearTimeout(timer.timerId);
        }
      }
      timers.clear();
      break;
    }
  }
};
`

interface WorkerTimer {
  /** 设置不受后台节流的 setInterval */
  setInterval: (callback: () => void, delay: number) => number
  /** 设置不受后台节流的 setTimeout */
  setTimeout: (callback: () => void, delay: number) => number
  /** 清除 interval */
  clearInterval: (id: number) => void
  /** 清除 timeout */
  clearTimeout: (id: number) => void
  /** 销毁 Worker，清除所有定时器 */
  terminate: () => void
  /** Worker 是否可用（某些环境可能不支持） */
  isAvailable: boolean
}

/**
 * 创建基于 Web Worker 的后台定时器
 * 如果 Worker 不可用（如 SSR 环境），自动降级为主线程定时器
 */
export function createWorkerTimer(): WorkerTimer {
  let worker: Worker | null = null
  let nextId = 1
  // 回调映射：workerTimer ID → 回调函数
  const callbacks = new Map<number, () => void>()
  // 降级模式下的原生定时器 ID 映射：workerTimer ID → native timer ID
  const nativeTimerIds = new Map<number, number>()
  let isAvailable = false

  try {
    // 创建 inline Worker（无需额外文件）
    const blob = new Blob([WORKER_CODE], { type: 'application/javascript' })
    const url = URL.createObjectURL(blob)
    worker = new Worker(url)
    URL.revokeObjectURL(url) // Worker 创建后即可释放 URL

    worker.onmessage = (e) => {
      const { type, id } = e.data
      if (type === 'tick') {
        const cb = callbacks.get(id)
        if (cb) {
          try {
            cb()
          }
          catch (err) {
            console.warn('⚠️ WorkerTimer 回调执行出错:', err)
          }
        }
      }
    }

    worker.onerror = (err) => {
      console.warn('⚠️ WorkerTimer Worker 出错:', err)
    }

    isAvailable = true
    console.debug('⚙️ Web Worker 后台定时器已创建（定时器不受后台节流影响）')
  }
  catch (err) {
    console.warn('⚠️ Web Worker 创建失败，降级为主线程定时器:', err)
    worker = null
  }

  return {
    isAvailable,

    setInterval(callback: () => void, delay: number): number {
      const id = nextId++
      callbacks.set(id, callback)

      if (worker) {
        worker.postMessage({ type: 'setInterval', id, delay })
      }
      else {
        // 降级：主线程 setInterval（会被后台节流）
        const nativeId = window.setInterval(callback, delay)
        nativeTimerIds.set(id, nativeId)
      }

      return id
    },

    setTimeout(callback: () => void, delay: number): number {
      const id = nextId++
      // 包装回调：执行后自动清理 Map 条目
      callbacks.set(id, () => {
        callback()
        callbacks.delete(id)
        nativeTimerIds.delete(id)
      })

      if (worker) {
        worker.postMessage({ type: 'setTimeout', id, delay })
      }
      else {
        // 降级：主线程 setTimeout（会被后台节流）
        const nativeId = window.setTimeout(() => {
          const cb = callbacks.get(id)
          if (cb)
            cb() // wrapper 内部会清理 callbacks 和 nativeTimerIds
        }, delay)
        nativeTimerIds.set(id, nativeId)
      }

      return id
    },

    clearInterval(id: number): void {
      if (worker) {
        worker.postMessage({ type: 'clearInterval', id })
      }
      else {
        const nativeId = nativeTimerIds.get(id)
        if (nativeId !== undefined) {
          window.clearInterval(nativeId)
          nativeTimerIds.delete(id)
        }
      }
      callbacks.delete(id)
    },

    clearTimeout(id: number): void {
      if (worker) {
        worker.postMessage({ type: 'clearTimeout', id })
      }
      else {
        const nativeId = nativeTimerIds.get(id)
        if (nativeId !== undefined) {
          window.clearTimeout(nativeId)
          nativeTimerIds.delete(id)
        }
      }
      callbacks.delete(id)
    },

    terminate(): void {
      if (worker) {
        worker.postMessage({ type: 'clearAll' })
        worker.terminate()
        worker = null
      }
      else {
        // 降级模式：清理所有原生定时器
        for (const [, nativeId] of nativeTimerIds) {
          window.clearTimeout(nativeId) // clearTimeout 也能清 interval
        }
        nativeTimerIds.clear()
      }
      callbacks.clear()
      isAvailable = false
    },
  }
}
